import logo from '@/assets/logo-CRP.png'
import { Input } from '@/components/ui/Input'
import { ROUTES } from '@/constants/routes'
import { useAuth } from '@/features/auth/hooks/useAuth'
import {
  Box,
  Card,
  Fieldset,
  Heading,
  HStack,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FiLogIn } from 'react-icons/fi'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * Login Page
 * User authentication page
 */
export function Login() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { login, isLoading } = useAuth()
  const [serverErrors, setServerErrors] = useState({})

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm()

  const onSubmit = async (data) => {
    try {
      setServerErrors({})
      await login(data)
      // Redirect to the page user was trying to access or dashboard
      const from = location.state?.from?.pathname || ROUTES.DASHBOARD
      navigate(from, { replace: true })
    } catch (error) {
      // Show field-level errors under inputs
      if (error.error?.details) {
        setServerErrors(error.error.details)
      }
    }
  }

  return (
    <Box w="full" maxW="450px">
      <Card.Root
        bg="white"
        borderRadius="8px"
        boxShadow="0 4px 8px rgba(0,0,0,0.1)"
        border="1px solid #DFE1E6"
      >
        <Card.Body p={8}>
          <VStack spacing={6} as="form" onSubmit={handleSubmit(onSubmit)}>
            {/* Logo */}
            <Box mb={2}>
              <img
                src={logo}
                alt="Broker24 Logo"
                style={{ height: '50px', width: 'auto' }}
              />
            </Box>

            {/* Header */}
            <VStack spacing={2} w="full">
              <Heading size="xl" color="#172B4D" fontWeight="600">
                {t('auth.welcomeBack')}
              </Heading>
              <Text color="#5E6C84" fontSize="14px">
                {t('auth.signInToAccount')}
              </Text>
            </VStack>

            {/* Form Fields */}
            <Stack spacing={4} w="full">
              {/* Username */}
              <Fieldset.Root>
                <Fieldset.Legend fontSize="sm" fontWeight="500" color="#42526E" mb={2}>
                  {t('auth.username')}
                </Fieldset.Legend>
                <Input
                  type="text"
                  placeholder={t('auth.enterUsername')}
                  {...register('username', {
                    required: t('auth.usernameRequired'),
                  })}
                />
                {(errors.username || serverErrors.username) && (
                  <Text fontSize="xs" color="#DE350B" mt={1}>
                    {errors.username?.message || serverErrors.username?.[0]}
                  </Text>
                )}
              </Fieldset.Root>

              {/* Password */}
              <Fieldset.Root>
                <Fieldset.Legend fontSize="sm" fontWeight="500" color="#42526E" mb={2}>
                  {t('auth.password')}
                </Fieldset.Legend>
                <Input
                  type="password"
                  placeholder={t('auth.enterPassword')}
                  {...register('password', {
                    required: t('auth.passwordRequired'),
                  })}
                />
                {(errors.password || serverErrors.password) && (
                  <Text fontSize="xs" color="#DE350B" mt={1}>
                    {errors.password?.message || serverErrors.password?.[0]}
                  </Text>
                )}
              </Fieldset.Root>

              {/* Non-field errors */}
              {serverErrors.non_field_errors && (
                <Box
                  p={3}
                  bg="#FFEBE6"
                  borderRadius="4px"
                  border="1px solid #FF8B00"
                >
                  <Text fontSize="sm" color="#DE350B">
                    {serverErrors.non_field_errors[0]}
                  </Text>
                </Box>
              )}
            </Stack>

            {/* Submit Button */}
            <Box w="full" pt={2}>
              <HStack
                as="button"
                type="submit"
                w="full"
                justify="center"
                spacing={2}
                bg="#0052CC"
                color="white"
                py={3}
                px={4}
                borderRadius="4px"
                fontWeight="500"
                fontSize="14px"
                cursor="pointer"
                disabled={isLoading}
                opacity={isLoading ? 0.6 : 1}
                _hover={{ bg: '#0747A6' }}
                _active={{ bg: '#0747A6' }}
                transition="all 0.2s"
              >
                <Box as={FiLogIn} />
                <span>{isLoading ? t('auth.signingIn') : t('auth.login')}</span>
              </HStack>
            </Box>
          </VStack>
        </Card.Body>
      </Card.Root>
    </Box>
  )
}

export default Login
